import * as SliderPrimitive from '@radix-ui/react-slider'
import React from 'react'

import { cn } from '../../lib/utils'

function Slider({
  className,
  defaultValue,
  value,
  min = 0,
  max = 100,
  ...props
}: React.ComponentProps<typeof SliderPrimitive.Root>) {
  const _values = React.useMemo(
    () =>
      Array.isArray(value)
        ? value
        : Array.isArray(defaultValue)
          ? defaultValue
          : [min, max],
    [value, defaultValue, min, max],
  )

  return (
    <SliderPrimitive.Root
      data-slot="slider"
      defaultValue={defaultValue}
      value={value}
      min={min}
      max={max}
      className={cn(
        [
          'group relative flex w-full touch-none items-center select-none',
          'data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed',
          'data-[orientation=vertical]:h-full',
          'data-[orientation=vertical]:min-h-44',
          'data-[orientation=vertical]:w-auto',
          'data-[orientation=vertical]:flex-col',
        ].join(' '),
        className,
      )}
      {...props}
    >
      <SliderPrimitive.Track
        data-slot="slider-track"
        className={cn(
          [
            'bg-panel-3 relative grow overflow-hidden rounded-full',
            'data-[orientation=horizontal]:h-1.5',
            'data-[orientation=horizontal]:w-full',
            'data-[orientation=vertical]:h-full',
            'data-[orientation=vertical]:w-1.5',
          ].join(' '),
        )}
      >
        <SliderPrimitive.Range
          data-slot="slider-range"
          className={cn(
            [
              'bg-accent absolute',
              // When the parent root is disabled, mute the active range so
              // it stops reading as a live cyan fill.
              'group-data-[disabled]:bg-text-mute',
              'data-[orientation=horizontal]:h-full',
              'data-[orientation=vertical]:w-full',
            ].join(' '),
          )}
        />
      </SliderPrimitive.Track>
      {Array.from({ length: _values.length }, (_, index) => (
        <SliderPrimitive.Thumb
          data-slot="slider-thumb"
          key={index}
          className={[
            'border-border-strong bg-accent ring-ring/50 block size-4 shrink-0 rounded-full border shadow-xs',
            'cursor-grab active:cursor-grabbing',
            'transition-[color,box-shadow] hover:bg-accent-hi focus-visible:bg-accent focus-visible:outline-hidden',
            'group-data-[disabled]:bg-text-mute group-data-[disabled]:hover:bg-text-mute',
            'disabled:pointer-events-none disabled:opacity-50',
          ].join(' ')}
        />
      ))}
    </SliderPrimitive.Root>
  )
}

export { Slider }
